import { forwardRef } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { SERVICE_ICON_MAP, SERVICE_ONLY_ICON_MAP } from '@hh.ru/magritte-ui-branded-button/icons';
import type { BrandedButtonProps } from '@hh.ru/magritte-ui-branded-button/types';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './branded-button.less';

const ICONS_MAX = 2;

const BrandedButtonComponent: PolymorphicForwardRefRenderFunc<BrandedButtonProps, 'button'> = (
    {
        Element = 'button',
        service: _service,
        size = 'medium',
        mode = 'primary',
        'data-qa': dataQa = 'branded-button',
        disabled,
        loading,
        children,
        ...restProps
    },
    ref
) => {
    const ICON_MAP = children ? SERVICE_ICON_MAP : SERVICE_ONLY_ICON_MAP;
    const service = Array.isArray(_service) && _service.length === 1 ? _service[0] : _service;
    const isMoreButton = Array.isArray(service);
    const forceDisabled = useDisabled();
    const isComponentDisabled = disabled || forceDisabled;
    const state = isComponentDisabled ? 'disabled' : 'default';
    const IconComponent = isMoreButton ? ICON_MAP.more?.[mode][state] : ICON_MAP[service]?.[mode][state];
    const serviceClass = (isMoreButton ? `more-${mode}` : `${service}-${mode}`) as keyof typeof styles;
    const additionalAttributes: Record<string, unknown> = {
        'data-qa': dataQa,
    };

    if (Element === 'button') {
        additionalAttributes.type = 'button';
    }

    if (ref) {
        additionalAttributes.ref = ref;
    }

    return (
        <Element
            {...additionalAttributes}
            {...restProps}
            disabled={isComponentDisabled}
            tabIndex={isComponentDisabled || loading ? -1 : 0}
            className={classnames(styles.brandedButton, styles[size], serviceClass && styles[serviceClass], {
                [styles.withLabel]: !!children,
                [styles.disabled]: isComponentDisabled,
                [styles.withLoading]: loading,
            })}
        >
            <div className={classnames(styles.content)}>
                {children && (
                    <span className={styles.label}>
                        <Text Element="span" typography="subtitle-1-semibold">
                            {children}
                        </Text>
                    </span>
                )}
                {IconComponent && (
                    <span className={styles.icon}>
                        <IconComponent data-qa={`branded-button-icon-${isMoreButton ? 'more' : service}`} />
                    </span>
                )}
                {isMoreButton && !IconComponent && (
                    <div className={styles.icons}>
                        {service.slice(0, ICONS_MAX).map((item) => {
                            const Icon =
                                mode === 'tertiary'
                                    ? ICON_MAP[item]?.tertiary[state]
                                    : ICON_MAP[item]?.secondary[state];
                            return (
                                <div key={item} className={styles[`icon-${item}`]}>
                                    {Icon && <Icon data-qa={`branded-button-icon-${item}`} />}
                                </div>
                            );
                        })}
                        {service.length > ICONS_MAX && (
                            <Text typography="subtitle-1-semibold">+ {service.length - ICONS_MAX}</Text>
                        )}
                    </div>
                )}
                {loading && (
                    <span className={styles.loader}>
                        <Loader size={24} />
                    </span>
                )}
            </div>
        </Element>
    );
};

export const BrandedButton: PolymorphicComponentWithRef<BrandedButtonProps, 'button'> =
    forwardRef(BrandedButtonComponent);
